import { buildTextInputFieldData } from 'adapter/in/common/form/Text';
import { OPTIONS } from 'adapter/in/common/widget/options';
import {
    mapCustomRangeFieldData,
    rangesHaveOptionalData,
} from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options/Range';
import useArrayField, { RemoveElementRHF } from 'adapter/in/common/hooks/useArrayField';
import useCounter from 'adapter/in/dashboards/utils/useCounter';
import GaugeData from 'domain/entity/widgets/GaugeData';
import { pipe } from 'fp-ts/lib/function';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { WidgetType } from 'domain/entity/widget';
import { WidgetDataTypes } from 'domain/entity/widgetData';
import { usePrevious } from 'adapter/in/common/hooks/usePrevious';
import { useCurrentWidgetConfiguration } from 'adapter/in/common/context/currentWidgetConfiguration';
import { ConfigFieldType, FieldStatus, GaugeRangeValue } from 'adapter/in/common/form/types';

const DEFAULT_COLORS_RANGES = [
    '#00CC87',
    '#FB8F38',
    '#F25A5A',
    '#5DA8F2',
    '#0054A1',
    '#FFC400',
    '#DD4B80',
    '#38DCDF',
    '#17A994',
    '#9152A5',
    '#FFACA8',
    '#9C755F',
    '#9FA3AA',
];

export type ItemGaugeRangeValue = GaugeRangeValue & { id: string };

type useRangeContainerProps = {
    fields: ItemGaugeRangeValue[];
    showReset: boolean;
    getValue: (fieldName: string) => GaugeRangeValue;
    handleRemoveRange: (index?: RemoveElementRHF) => void;
    handleAddRange: () => void;
    handleResetRanges: () => void;
};

const useRangeContainer = (name: string, data: WidgetDataTypes): useRangeContainerProps => {
    const { min } = data.type === WidgetType.GAUGE ? (data as GaugeData) : { min: 0 };
    const { fields, getValues, handleAddElement, handleRemoveElement } = useArrayField<GaugeRangeValue>(name);
    const { count, increment } = useCounter(0);
    const { watch, setValue, clearErrors } = useFormContext();
    const [showReset, setShowReset] = useState(false);
    const fieldsUpdated = watch(name);
    const { widgetId } = useCurrentWidgetConfiguration();
    const prevWidgetId = usePrevious(widgetId);
    const [colors, setColors] = useState<string[]>([]);
    const [loadedColors, setLoadedColors] = useState<boolean>();

    useEffect(() => {
        if (fieldsUpdated !== undefined && data.type === WidgetType.GAUGE) {
            const ranges = rangesHaveOptionalData((fieldsUpdated as unknown) as GaugeRangeValue[]);
            setShowReset(ranges.length > 0 || fieldsUpdated.length !== (data as GaugeData).ranges.length);
        }
    }, [fieldsUpdated, data]);

    useEffect(() => {
        if (colors.length === 0 && fieldsUpdated !== undefined) {
            setLoadedColors(true);
        }
    }, [fieldsUpdated, colors]);

    useEffect(() => {
        if (loadedColors) {
            const ranges = (getValues(`${OPTIONS.RANGES}`) as unknown) as GaugeRangeValue[];
            setColors(DEFAULT_COLORS_RANGES.filter((color) => !ranges.map((range) => range.color.value).includes(color)));
        }
    }, [loadedColors]);

    const handleResetRanges = useCallback(() => {
        const castFields = (fieldsUpdated as unknown) as GaugeRangeValue[];
        castFields.forEach((_, index) => handleRemoveElement(index));
        pipe(mapCustomRangeFieldData({ isDefaultData: true, ranges: (data as GaugeData).ranges }), (values) =>
            setValue(OPTIONS.RANGES, values)
        );
        clearErrors();
    }, [data, fieldsUpdated]);

    const handleAddRange = useCallback(() => {
        const color = count >= colors.length ? '#000000' : colors[count];
        const castFields = (fieldsUpdated as unknown) as GaugeRangeValue[];
        const from = castFields.length === 0 ? String(min) : castFields[castFields.length - 1].to.value;
        handleAddElement({
            name: buildTextInputFieldData(FieldStatus.INITIAL_VALUE, '', ConfigFieldType.OPTIONS, ''),
            color: buildTextInputFieldData(FieldStatus.HAS_VALUE, color, ConfigFieldType.OPTIONS, color),
            from: buildTextInputFieldData(FieldStatus.HAS_VALUE, from, ConfigFieldType.OPTIONS, from),
            to: buildTextInputFieldData(FieldStatus.INITIAL_VALUE, '', ConfigFieldType.OPTIONS, ''),
        });
        clearErrors();
        increment();
    }, [colors, count, fieldsUpdated]);

    const handleRemoveRange = useCallback(
        (index?: RemoveElementRHF) => {
            handleRemoveElement(index);
            const castFields = (getValues(OPTIONS.RANGES) as unknown) as GaugeRangeValue[];
            const filteredRanges = castFields.map((range) => ({
                name: buildTextInputFieldData(FieldStatus.HAS_VALUE, range.name.initialValue, ConfigFieldType.OPTIONS, range.name.value),
                color: buildTextInputFieldData(FieldStatus.HAS_VALUE, range.color.initialValue, ConfigFieldType.OPTIONS, range.color.value),
                from: buildTextInputFieldData(
                    FieldStatus.HAS_VALUE,
                    String(range.from.initialValue),
                    ConfigFieldType.OPTIONS,
                    String(range.from.value)
                ),
                to: buildTextInputFieldData(
                    FieldStatus.HAS_VALUE,
                    String(range.to.initialValue),
                    ConfigFieldType.OPTIONS,
                    String(range.to.value)
                ),
            }));
            setValue(OPTIONS.RANGES, filteredRanges);
            clearErrors();
        },
        [count]
    );

    return {
        fields: widgetId === prevWidgetId ? (fields as ItemGaugeRangeValue[]) : [],
        showReset,
        getValue: (fieldName: string) => getValues(fieldName),
        handleAddRange,
        handleResetRanges,
        handleRemoveRange,
    };
};

export default useRangeContainer;
